export interface CorrectionEntry {
  id: string;
  source: string;
  replacement: string;
  createdAt: number;
  updatedAt: number;
}

const STORAGE_KEY = "speechdraft.corrections.v1";
const WORD_BOUNDARY_PATTERN = "[^\\p{L}\\p{N}]";

interface StoredCorrections {
  version: 1;
  entries: CorrectionEntry[];
}

export function loadCorrectionDictionary(): CorrectionEntry[] {
  try {
    const storedValue = window.localStorage?.getItem(STORAGE_KEY);
    if (!storedValue) {
      return [];
    }

    const payload = JSON.parse(storedValue) as Partial<StoredCorrections>;
    if (!Array.isArray(payload.entries)) {
      return [];
    }

    return payload.entries
      .map(normalizeCorrectionEntry)
      .filter((entry): entry is CorrectionEntry => Boolean(entry))
      .sort((left, right) => right.updatedAt - left.updatedAt);
  } catch {
    return [];
  }
}

export function saveCorrectionDictionary(entries: CorrectionEntry[]): void {
  try {
    const payload: StoredCorrections = {
      version: 1,
      entries: entries
        .map(normalizeCorrectionEntry)
        .filter((entry): entry is CorrectionEntry => Boolean(entry))
    };
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Corrections are a convenience layer; the editor still works if storage is unavailable.
  }
}

export function upsertCorrection(
  entries: CorrectionEntry[],
  source: string,
  replacement: string
): CorrectionEntry[] {
  const normalizedSource = normalizeCorrectionPhrase(source);
  const normalizedReplacement = replacement.trim();
  if (!normalizedSource || !normalizedReplacement) {
    return entries;
  }

  const now = Date.now();
  const existingEntry = entries.find(
    (entry) => normalizeCorrectionPhrase(entry.source).toLowerCase() === normalizedSource.toLowerCase()
  );

  if (existingEntry) {
    return entries
      .map((entry) =>
        entry.id === existingEntry.id
          ? {
              ...entry,
              source: normalizedSource,
              replacement: normalizedReplacement,
              updatedAt: now
            }
          : entry
      )
      .sort((left, right) => right.updatedAt - left.updatedAt);
  }

  return [
    {
      id: createCorrectionId(),
      source: normalizedSource,
      replacement: normalizedReplacement,
      createdAt: now,
      updatedAt: now
    },
    ...entries
  ];
}

export function applyCorrectionDictionary(text: string, entries: CorrectionEntry[]): string {
  if (!text || entries.length === 0) {
    return text;
  }

  return [...entries]
    .filter((entry) => entry.source.trim() && entry.replacement.trim())
    .sort((left, right) => right.source.length - left.source.length)
    .reduce((value, entry) => applyCorrection(value, entry), text);
}

export function normalizeCorrectionPhrase(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function applyCorrection(text: string, entry: CorrectionEntry): string {
  const source = normalizeCorrectionPhrase(entry.source);
  const replacement = entry.replacement.trim();
  if (!source || !replacement) {
    return text;
  }

  const sourcePattern = escapeRegExp(source).replace(/\s+/g, "\\s+");
  const leadingBoundary = startsWithWordCharacter(source) ? `(^|${WORD_BOUNDARY_PATTERN})` : "()";
  const trailingBoundary = endsWithWordCharacter(source) ? `(?=$|${WORD_BOUNDARY_PATTERN})` : "";
  const pattern = new RegExp(`${leadingBoundary}${sourcePattern}${trailingBoundary}`, "giu");

  return text.replace(pattern, (_match, prefix: string) => `${prefix}${replacement}`);
}

function normalizeCorrectionEntry(value: unknown): CorrectionEntry | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const entry = value as Partial<CorrectionEntry>;
  const source = normalizeCorrectionPhrase(String(entry.source ?? ""));
  const replacement = String(entry.replacement ?? "").trim();
  if (!source || !replacement) {
    return null;
  }

  const createdAt = typeof entry.createdAt === "number" ? entry.createdAt : Date.now();
  const updatedAt = typeof entry.updatedAt === "number" ? entry.updatedAt : createdAt;

  return {
    id: String(entry.id || createCorrectionId()),
    source,
    replacement,
    createdAt,
    updatedAt
  };
}

function startsWithWordCharacter(value: string): boolean {
  return /^[\p{L}\p{N}]/u.test(value);
}

function endsWithWordCharacter(value: string): boolean {
  return /[\p{L}\p{N}]$/u.test(value);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function createCorrectionId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-correction-${Math.random().toString(16).slice(2)}`
  );
}
